<script setup>
import EyeIcon from '@iconify-vue/ph/eye';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import DangerIconCell from '@js/components/tables/cells/DangerIconCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const groupLabels = {
    ecommerce: 'E-commerce',
    google_my_business: 'Google My Business',
    guaranteed_reviews: 'Avis Garantis',
    verified_reviews: 'Avis Vérifiés',
    page_speed: 'Page Speed',
    supplier_costs: 'Coûts fournisseurs',
};

const { tenant, all } = defineProps({
    tenant: {
        type: Object,
        required: false,
    },
    all: {
        type: Boolean,
        required: false,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'tenant',
            header: () => 'Client',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.tenant,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'group',
            header: () => 'Groupe',
            cell: (props) =>
                h(TextCell, {
                    text: groupLabels[props.row.original.group],
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'message',
            header: () => 'Message',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.message,
                    length: 300,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'severity',
            header: () => 'Gravité',
            cell: (props) =>
                h(DangerIconCell, {
                    color:
                        props.row.original.severity === 1
                            ? 'green'
                            : props.row.original.severity === 2
                              ? 'yellow'
                              : 'red',
                }),
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            h(ActionButton, {
                                size: 'small',
                                square: true,
                                title: 'Voir',
                                href: route('front.projectManagement.alerts', {
                                    tenant: props.row.original.tenant.id,
                                }),
                                icon: EyeIcon,
                            }),
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.postAlerts', {
        tenant: tenant ? tenant.id : null,
        all,
    }),
    columnVisibility: {
        'tenant.name': !tenant,
        id: !!tenant,
    },
    columnPinning: {
        right: ['actions'],
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    />
</template>
